import type { CollabEnvelope } from '../types/envelope.js';
import { validateEnvelope } from '../validation/envelope-validation.js';
import type { LinkAdapter } from '../types/transport.js';
import type { LinkKind } from '../types/routing.js';

interface ChannelMessage {
  data: unknown;
}

interface ChannelPort {
  onmessage: ((event: ChannelMessage) => void) | null;
  postMessage(data: unknown): void;
  close(): void;
}

export type ChannelFactory = (channelName: string) => ChannelPort;

interface RuntimeChannel {
  onmessage: ((event: { data: unknown }) => void) | null;
  postMessage(data: unknown): void;
  close(): void;
}

interface ChannelConstructor {
  new (name: string): RuntimeChannel;
}

function isChannelConstructor(value: unknown): value is ChannelConstructor {
  return typeof value === 'function';
}

function createDefaultChannel(channelName: string): ChannelPort {
  const candidate = Reflect.get(globalThis, 'BroadcastChannel');
  if (!isChannelConstructor(candidate)) {
    throw new Error('BroadcastChannel is not available in this runtime');
  }
  const channel = new candidate(channelName);
  const wrapper: ChannelPort = {
    onmessage: null,
    postMessage: (d) => channel.postMessage(d),
    close: () => channel.close(),
  };
  channel.onmessage = (event) => {
    const handler = wrapper.onmessage;
    if (handler !== null) {
      handler({ data: event.data });
    }
  };
  return wrapper;
}

export class BroadcastChannelLink implements LinkAdapter {
  readonly kind: LinkKind = 'custom';
  readonly reliable = false;
  readonly mtu: number;

  private readonly channel: ChannelPort;
  private closed = false;

  constructor(
    readonly channelName: string,
    private readonly onEnvelope: (envelope: CollabEnvelope<unknown>) => void | Promise<void>,
    channelFactory: ChannelFactory = createDefaultChannel,
    mtu = 16 * 1024 * 1024,
  ) {
    if (channelName.trim().length === 0) {
      throw new Error('BroadcastChannel name cannot be empty');
    }
    if (!Number.isInteger(mtu) || mtu <= 0) {
      throw new Error('BroadcastChannel logical MTU must be positive integer');
    }

    this.mtu = mtu;
    this.channel = channelFactory(channelName);

    this.channel.onmessage = (event) => {
      if (this.closed) return;

      try {
        const envelope = validateEnvelope(event.data);
        void Promise.resolve(this.onEnvelope(envelope)).catch(() => {
          // The receiving bus owns application-level error handling.
        });
      } catch {
        // Invalid wire data dropped at boundary.
      }
    };
  }

  isAvailable(): boolean {
    return !this.closed;
  }

  async send(envelope: CollabEnvelope<unknown>): Promise<void> {
    if (this.closed) throw new Error('BroadcastChannelLink is closed');
    this.channel.postMessage(envelope);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.channel.onmessage = null;
    this.channel.close();
  }
}
